import { useId, useRef, useState } from "react";
import { AlertCircle, ImagePlus, Play, Plus, Star, Trash2, Upload } from "lucide-react";
import type { MidiaDaBibliotecaDto } from "@base/contracts";
import { EstadoVazio, MolduraDoApp } from "@base/ui";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageLayout } from "@/components/layout/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { LogoRenkeRefs } from "../../../shared/branding/logo-renke-refs";
import { usePropsDoSeletorNaMoldura } from "../../../shared/tenant/projeto.contexto";
import { useDialogos } from "../../../shared/ui/dialogos.contexto";
import { MidiaAutenticada } from "../../referencias/presentation/midia-autenticada";
import { useNavegacaoDoApp } from "../../referencias/presentation/navegacao-do-app";
import { rodapeDaSessaoNaLateral } from "../../referencias/presentation/rodape-da-sessao";
import {
  useAtualizarFavorita,
  useEnviarMidias,
  useExcluirMidia,
  useListarMidias,
  usePodeEscreverMidias,
} from "../application/use-midias";
import {
  ACEITE_DA_BIBLIOTECA,
  filtrarArquivosDaBiblioteca,
} from "../domain/arquivos-da-biblioteca";
import {
  ehVideoDaBiblioteca,
  FILTROS_INICIAIS_DA_BIBLIOTECA,
  filtrosDaBibliotecaAtivos,
  rotuloDoTipo,
  toFiltroListarMidias,
  type FiltrosDaBiblioteca,
} from "../domain/rotulos-da-biblioteca";
import { BarraFiltrosBiblioteca } from "./barra-filtros-biblioteca";
import { LightboxBiblioteca } from "./lightbox-biblioteca";
import { OverlaySoltarArquivos } from "./overlay-soltar-arquivos";
import { useSoltarArquivos } from "./use-soltar-arquivos";
import { useValorDebounced } from "./use-valor-debounced";

export function PaginaBiblioteca() {
  const seletor = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("biblioteca");
  const idArquivos = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const podeEscrever = usePodeEscreverMidias();
  const [busca, definirBusca] = useState("");
  const [filtros, definirFiltros] = useState<FiltrosDaBiblioteca>(
    FILTROS_INICIAIS_DA_BIBLIOTECA,
  );
  const [abertoId, definirAbertoId] = useState<string | null>(null);
  const [itemAbertoCache, definirItemAbertoCache] =
    useState<MidiaDaBibliotecaDto | null>(null);
  const q = useValorDebounced(busca, 250);
  const filtroApi = toFiltroListarMidias({ ...filtros, q });
  const filtrosAtivos = filtrosDaBibliotecaAtivos({ ...filtros, q: busca });
  const { data, isLoading, isError, error } = useListarMidias(filtroApi);
  const enviar = useEnviarMidias();
  const excluir = useExcluirMidia();
  const favoritar = useAtualizarFavorita();
  const { confirmar, avisar } = useDialogos();

  const itens = data?.itens ?? [];
  const vazio = !isLoading && !isError && itens.length === 0 && !filtrosAtivos;
  const vazioFiltrado =
    !isLoading && !isError && itens.length === 0 && filtrosAtivos;
  const indiceAberto = itens.findIndex((item) => item.id === abertoId);
  const itemAberto =
    (indiceAberto >= 0 ? itens[indiceAberto] : null) ?? itemAbertoCache;

  async function aoEscolher(lista: File[]) {
    const { aceitos, recusados } = filtrarArquivosDaBiblioteca(lista);
    try {
      if (aceitos.length > 0) {
        await enviar.mutateAsync(aceitos);
      }
    } catch (causa) {
      await avisar({
        titulo: "Não deu para enviar",
        descricao:
          causa instanceof Error
            ? causa.message
            : "Revise o formato. Use jpg, png, mp4 ou mov.",
      });
      return;
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
    if (recusados.length > 0) {
      await avisar({
        titulo:
          aceitos.length > 0
            ? "Alguns arquivos ficaram de fora"
            : "Esse arquivo não entra",
        descricao: "Use jpg, png, mp4 ou mov.",
      });
    }
  }

  const soltar = useSoltarArquivos({
    ativo: podeEscrever && !enviar.isPending,
    onSoltar: (arquivos) => {
      void aoEscolher(arquivos);
    },
  });

  async function aoExcluir(item: MidiaDaBibliotecaDto) {
    const ok = await confirmar({
      titulo: "Excluir da biblioteca?",
      descricao:
        "Some daqui. Os posts que já usaram este arquivo continuam no ar.",
      rotuloConfirmar: "Excluir",
      rotuloCancelar: "Cancelar",
      tomConfirmar: "perigo",
    });
    if (!ok) return;
    try {
      await excluir.mutateAsync(item.id);
      if (abertoId === item.id) {
        definirAbertoId(null);
        definirItemAbertoCache(null);
      }
    } catch (causa) {
      await avisar({
        titulo: "Não excluiu",
        descricao:
          causa instanceof Error ? causa.message : "Tente de novo em instantes.",
      });
    }
  }

  async function aoFavoritar(item: MidiaDaBibliotecaDto, favorita: boolean) {
    try {
      const atualizado = await favoritar.mutateAsync({
        id: item.id,
        favorita,
      });
      if (itemAbertoCache?.id === item.id) {
        definirItemAbertoCache(atualizado);
      }
    } catch (causa) {
      await avisar({
        titulo: "Não atualizou o favorito",
        descricao:
          causa instanceof Error ? causa.message : "Tente de novo em instantes.",
      });
    }
  }

  function abrirPreview(item: MidiaDaBibliotecaDto) {
    definirAbertoId(item.id);
    definirItemAbertoCache(item);
  }

  function abrirSeletor() {
    inputRef.current?.click();
  }

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Biblioteca"
      subtitulo="Fotos e vídeos deste projeto, prontos para reusar no agendamento."
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletor}
      acoes={
        podeEscrever ? (
          <>
            <input
              ref={inputRef}
              id={idArquivos}
              type="file"
              accept={ACEITE_DA_BIBLIOTECA}
              multiple
              className="sr-only"
              onChange={(e) =>
                void aoEscolher(Array.from(e.target.files ?? []))
              }
            />
            <Button
              type="button"
              variant={vazio ? "outline" : "default"}
              className="cursor-pointer"
              disabled={enviar.isPending}
              onClick={abrirSeletor}
            >
              <Upload className="size-4" aria-hidden />
              {enviar.isPending ? "Enviando…" : "Enviar arquivos"}
            </Button>
          </>
        ) : undefined
      }
    >
      <div
        data-testid="zona-soltar-biblioteca"
        className="min-h-[min(32rem,calc(100dvh-10rem))]"
      >
      <PageLayout className="animate-aparecer space-y-6">
        <OverlaySoltarArquivos visivel={soltar.sobre} />

        {enviar.isPending ? (
          <div
            role="status"
            aria-live="polite"
            className="space-y-2 rounded-xl border bg-card px-5 py-4"
          >
            <p className="text-sm font-medium text-foreground">
              Enviando arquivos…
            </p>
            <p className="text-sm text-muted-foreground">
              Pode soltar o mouse. A gente termina daqui.
            </p>
            <div className="h-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full w-1/3 bg-primary motion-safe:animate-pulse motion-reduce:w-full" />
            </div>
          </div>
        ) : null}

        {filtrosAtivos || itens.length > 0 ? (
          <BarraFiltrosBiblioteca
            busca={busca}
            onBusca={definirBusca}
            filtros={filtros}
            onFiltros={definirFiltros}
            quantidade={itens.length}
          />
        ) : null}

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <Skeleton className="aspect-[4/5] rounded-xl" />
            <Skeleton className="aspect-[4/5] rounded-xl" />
            <Skeleton className="aspect-[4/5] rounded-xl" />
          </div>
        ) : null}

        {isError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>Não carregou a biblioteca</AlertTitle>
            <AlertDescription>
              {(error as Error)?.message ?? "Tente novamente."}
            </AlertDescription>
          </Alert>
        ) : null}

        {vazio ? (
          <EstadoVazio
            className={cn(
              "min-h-[min(28rem,70vh)] border-2 px-8 py-16 transition-colors duration-200",
              soltar.sobre && "border-primary bg-primary/5",
            )}
            icone={<ImagePlus aria-hidden />}
            titulo="Ainda não tem arquivo neste projeto"
            descricao="Arraste uma foto ou um vídeo para cá. Ou clique no botão e escolha no computador."
            acao={
              podeEscrever ? (
                <>
                  <Button
                    type="button"
                    className="cursor-pointer"
                    disabled={enviar.isPending}
                    onClick={abrirSeletor}
                  >
                    <Upload className="size-4" aria-hidden />
                    Adicionar arquivos
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Aceita jpg, png, mp4 e mov.
                  </p>
                </>
              ) : undefined
            }
          />
        ) : null}

        {vazioFiltrado ? (
          <EstadoVazio
            icone={<ImagePlus aria-hidden />}
            titulo="Nada com esse filtro"
            descricao="Tente outro nome, ou limpe a busca para ver o acervo de novo."
            acao={
              <Button
                type="button"
                variant="outline"
                className="cursor-pointer"
                onClick={() => {
                  definirBusca("");
                  definirFiltros(FILTROS_INICIAIS_DA_BIBLIOTECA);
                }}
              >
                Limpar filtros
              </Button>
            }
          />
        ) : null}

        {itens.length > 0 ? (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {itens.map((item) => (
              <li key={item.id}>
                <Card className="group overflow-hidden py-0 transition-shadow duration-200 hover:shadow-md">
                  <CardContent className="space-y-3 p-0">
                    <div className="relative aspect-[4/5] bg-muted">
                      <button
                        type="button"
                        className="absolute inset-0 cursor-pointer"
                        aria-label={`Ver ${item.nome}`}
                        onClick={() => abrirPreview(item)}
                      >
                        <MidiaAutenticada
                          src={item.capaUrl}
                          tipo="image"
                          alt=""
                          className="size-full object-cover"
                        />
                      </button>
                      {ehVideoDaBiblioteca(item) ? (
                        <span className="pointer-events-none absolute bottom-2 left-2 flex size-8 items-center justify-center rounded-full bg-background/80 text-foreground">
                          <Play className="size-4" aria-hidden />
                        </span>
                      ) : null}
                      <div className="absolute inset-x-0 top-0 flex justify-between p-2">
                        {podeEscrever ? (
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="cursor-pointer size-8 shadow-sm"
                            aria-pressed={item.favorita}
                            aria-label={
                              item.favorita
                                ? `Tirar ${item.nome} dos favoritos`
                                : `Marcar ${item.nome} como favorito`
                            }
                            disabled={favoritar.isPending}
                            onClick={() =>
                              void aoFavoritar(item, !item.favorita)
                            }
                          >
                            <Star
                              className="size-4"
                              aria-hidden
                              fill={item.favorita ? "currentColor" : "none"}
                            />
                          </Button>
                        ) : item.favorita ? (
                          <span className="flex size-8 items-center justify-center text-foreground">
                            <Star
                              className="size-4"
                              aria-hidden
                              fill="currentColor"
                            />
                          </span>
                        ) : (
                          <span />
                        )}
                        {podeEscrever ? (
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="cursor-pointer size-8 shadow-sm opacity-100 transition-opacity duration-200 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                            aria-label={`Excluir ${item.nome}`}
                            disabled={excluir.isPending}
                            onClick={() => void aoExcluir(item)}
                          >
                            <Trash2 className="size-4" aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    <div className="space-y-1 px-3 pb-3">
                      <p className="truncate text-sm font-medium text-foreground">
                        {item.nome}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {rotuloDoTipo(item)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
            {podeEscrever ? (
              <li>
                <button
                  type="button"
                  disabled={enviar.isPending}
                  onClick={abrirSeletor}
                  className={cn(
                    "flex aspect-[4/5] w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-input bg-card px-4 text-center transition-colors duration-200",
                    "hover:border-foreground/30 hover:bg-muted/40",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "disabled:pointer-events-none disabled:opacity-50",
                  )}
                >
                  <span className="flex size-10 items-center justify-center rounded-full bg-muted text-foreground">
                    <Plus className="size-5" aria-hidden />
                  </span>
                  <span className="space-y-1">
                    <span className="block text-sm font-medium text-foreground">
                      Adicionar
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Ou arraste para a página
                    </span>
                  </span>
                </button>
              </li>
            ) : null}
          </ul>
        ) : null}
      </PageLayout>
      </div>
      {itemAberto && abertoId ? (
        <LightboxBiblioteca
          item={itemAberto}
          temAnterior={indiceAberto > 0}
          temProximo={indiceAberto >= 0 && indiceAberto < itens.length - 1}
          podeFavoritar={podeEscrever}
          onFechar={() => {
            definirAbertoId(null);
            definirItemAbertoCache(null);
          }}
          onAnterior={() => {
            const anterior = itens[indiceAberto - 1];
            if (anterior) abrirPreview(anterior);
          }}
          onProximo={() => {
            const proximo = itens[indiceAberto + 1];
            if (proximo) abrirPreview(proximo);
          }}
          onFavoritar={(favorita) => {
            void aoFavoritar(itemAberto, favorita);
          }}
        />
      ) : null}
    </MolduraDoApp>
  );
}
